import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Target, Eye, Users, MapPin, Award, Heart } from "lucide-react";

const AboutSection = () => {
  const stats = [
    { value: "5,000+", label: "Active Runners", icon: Users },
    { value: "50+", label: "Corporate Partners", icon: Award },
    { value: "100+", label: "Volunteers", icon: Heart },
    { value: "3", label: "Cities", icon: MapPin }
  ];

  return (
    <section id="about" className="section-spacing bg-white" data-testid="about-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <p className="text-[#FF3B30] text-sm uppercase tracking-[0.2em] font-bold mb-4">
              About The FinRun
            </p>
            <h2 className="text-4xl sm:text-5xl font-bold text-[#0A0A0A] font-['Outfit'] mb-6 leading-tight">
              A Run With <br />
              <span className="text-[#007AFF]">A Mission</span>
            </h2>
            <p className="text-[#4B5563] text-lg mb-6 leading-relaxed">
              The FinRun is India's first and largest cyber awareness run, combining the spirit of 
              athleticism with a critical social mission. We're building a national movement where 
              every runner becomes a visible advocate for digital safety and trust.
            </p>
            <p className="text-[#4B5563] mb-8 leading-relaxed">
              Launched in Hyderabad with government support, we've expanded to Bengaluru and now 
              Chennai as our flagship edition. Our community of runners, tech professionals, and 
              cyber-aware citizens grows stronger with every stride.
            </p>

            {/* Mission & Vision */}
            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <div className="bg-[#FAFAFA] rounded-2xl p-6">
                <div className="w-12 h-12 bg-[#FF3B30]/10 rounded-xl flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-[#FF3B30]" />
                </div>
                <h3 className="font-bold text-[#0A0A0A] font-['Outfit'] mb-2">Our Mission</h3>
                <p className="text-[#4B5563] text-sm">
                  To educate, protect, and unite communities against the ever-evolving threats of cybercrimes.
                </p>
              </div>
              <div className="bg-[#FAFAFA] rounded-2xl p-6">
                <div className="w-12 h-12 bg-[#007AFF]/10 rounded-xl flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-[#007AFF]" />
                </div>
                <h3 className="font-bold text-[#0A0A0A] font-['Outfit'] mb-2">Our Vision</h3>
                <p className="text-[#4B5563] text-sm">
                  To build a safer digital world by empowering people to combat cybercrimes effectively.
                </p>
              </div>
            </div>

            <Link to="/register">
              <Button className="btn-primary" data-testid="about-register-btn">
                Join The Movement
              </Button>
            </Link>
          </div>

          {/* Right Content - Image & Stats */}
          <div className="relative">
            {/* Main Image - Real photo from Hyderabad 2024 */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_chennai-finrun/artifacts/jex5fex6_DR6A3777.JPG"
                alt="The FinRun Hyderabad 2024 - Participants with officials"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111827]/80 to-transparent" />
              
              {/* Quote Overlay */}
              <div className="absolute bottom-8 left-8 right-8 text-white">
                <p className="text-xl font-medium italic mb-2">
                  "Push forward, one stride at a time, and unlock your true potential."
                </p>
                <p className="text-[#FFD700] font-semibold">— The FinRun</p>
              </div>
            </div>

            {/* Floating Stats Card */}
            <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-xl p-6 max-w-[280px]">
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, i) => {
                  const Icon = stat.icon;
                  return (
                    <div key={i} className="text-center">
                      <Icon className="w-5 h-5 text-[#FF3B30] mx-auto mb-1" />
                      <p className="text-xl font-black font-['Outfit'] text-[#0A0A0A]">{stat.value}</p>
                      <p className="text-[#9CA3AF] text-xs">{stat.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Why Join Section */}
        <div className="mt-24">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-[#0A0A0A] font-['Outfit'] mb-4">
              Why Join The FinRun?
            </h3>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Purpose-Driven Mission",
                description: "Run for a cause that matters. Every step you take raises awareness about cybercrime and digital safety.",
                color: "#FF3B30"
              },
              {
                title: "Expert Insights",
                description: "Backed by cybersecurity experts, gain practical knowledge to protect yourself and your loved ones online.",
                color: "#007AFF"
              },
              {
                title: "Community Impact",
                description: "Join thousands of runners creating a movement that blends fitness with social responsibility.",
                color: "#FFD700"
              }
            ].map((item, i) => (
              <div 
                key={i} 
                className="bg-[#FAFAFA] rounded-2xl p-8 hover:shadow-lg transition-shadow"
              >
                <div 
                  className="w-2 h-12 rounded-full mb-6"
                  style={{ backgroundColor: item.color }}
                />
                <h4 className="text-xl font-bold text-[#0A0A0A] font-['Outfit'] mb-3">
                  {item.title}
                </h4>
                <p className="text-[#4B5563]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
